/**
 * CircularVideoBuffer.js — In-Memory Circular Rolling Video Buffer & Incident Assembler
 * 
 * Features:
 * - Records continuous screen activity using browser MediaRecorder API
 * - Permanently pins Chunk 0 (EBML container metadata & codec header)
 * - Retains a rolling 5-minute pre-incident window (~10 MB RAM footprint)
 * - Automatically prunes chunks older than maxBufferSeconds (300s)
 * - Freezes pre-incident buffer upon alert and captures post-incident window
 * - Assembles single coherent WebM evidence clip with accurate metadata
 * - Cleans up memory upon test completion (0 bytes uploaded if no alerts)
 */

class CircularVideoBuffer {
	constructor(stream, options = {}) {
		this.stream = stream;
		this.options = Object.assign({
			maxBufferSeconds: 120,      // 2 minutes pre-incident history (variable)
			postAlertSeconds: 180,      // 3 minutes post-incident capture (variable)
			timesliceMs: 5000,          // 5-second chunk intervals
			videoBitsPerSecond: 250000, // 250 kbps optimized proctoring bitrate
			mimeType: 'video/webm;codecs=vp9',
			onEvidenceReady: null
		}, options);

		this.mediaRecorder = null;
		this.pinnedHeader = null;       // Chunk 0 (Container Header)
		this.rollingChunks = [];        // Rolling FIFO array of { blob, timestamp, size }
		this.isRecording = false;

		// Incident tracking state
		this.activeIncident = null;     // { alertMeta, preChunks, postChunks, startTime, targetDuration }
		this.evidenceClips = [];

		this.handleDataAvailable = this.handleDataAvailable.bind(this);
		this.findClusterOffset = this.findClusterOffset.bind(this);
	}

	findClusterOffset(bytes) {
		// WebM Cluster ID signature is [0x1F, 0x43, 0xB6, 0x75]
		for (let i = 0; i <= bytes.length - 4; i++) {
			if (bytes[i] === 0x1f &&
				bytes[i + 1] === 0x43 &&
				bytes[i + 2] === 0xb6 &&
				bytes[i + 3] === 0x75) {
				return i;
			}
		}
		return -1;
	}

	start() {
		if (this.isRecording) return;

		let selectedMimeType = 'video/webm';
		if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
			if (MediaRecorder.isTypeSupported(this.options.mimeType)) {
				selectedMimeType = this.options.mimeType;
			} else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
				selectedMimeType = 'video/webm;codecs=vp8';
			}
		}

		const recorderOptions = {
			mimeType: selectedMimeType,
			videoBitsPerSecond: this.options.videoBitsPerSecond
		};

		try {
			this.mediaRecorder = new MediaRecorder(this.stream, recorderOptions);
		} catch (e) {
			console.warn("⚠️ [CircularVideoBuffer] Custom bitrate option failed, falling back to standard MediaRecorder:", e);
			this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: selectedMimeType });
		}

		this.mediaRecorder.ondataavailable = this.handleDataAvailable;
		this.mediaRecorder.start(this.options.timesliceMs);
		this.isRecording = true;

		console.log(`🎥 [CircularVideoBuffer] Started. Pre-buffer: ${this.options.maxBufferSeconds}s, Post-buffer: ${this.options.postAlertSeconds}s, Codec: ${selectedMimeType}`);
	}

	handleDataAvailable(event) {
		if (!event.data || event.data.size === 0) return;

		const now = Date.now();
		const chunkBlob = event.data;

		// 1. First chunk contains the container initialization header (EBML + Segment Info + Tracks)
		// AND the initial video frames of Question 1. We extract ONLY the pure metadata
		// header (bytes before Cluster 0x1F43B675) so Question 1 frames never get glued onto future clips!
		if (!this.pinnedHeader) {
			const sampleSlice = chunkBlob.slice(0, 4096);
			const processBuffer = (buffer) => {
				const uint8 = new Uint8Array(buffer);
				const clusterOffset = this.findClusterOffset(uint8);
				if (clusterOffset > 0) {
					this.pinnedHeader = chunkBlob.slice(0, clusterOffset, 'video/webm');
					console.log(`🎥 [CircularVideoBuffer] Pure EBML container header pinned (${this.pinnedHeader.size} bytes, excluded ${chunkBlob.size - clusterOffset} bytes initial video frames).`);

					// The remainder of Chunk 0 contains the actual video frames of Question 1.
					// Push it to rollingChunks so it rolls and ages out naturally after maxBufferSeconds!
					const videoBlob = chunkBlob.slice(clusterOffset, chunkBlob.size, 'video/webm');
					if (videoBlob.size > 0) {
						this.rollingChunks.push({
							blob: videoBlob,
							timestamp: now,
							size: videoBlob.size
						});
					}
				} else {
					console.warn("⚠️ [CircularVideoBuffer] Cluster tag not found in Chunk 0 prefix, falling back to full chunk.");
					this.pinnedHeader = chunkBlob;
				}
			};

			if (sampleSlice.arrayBuffer) {
				sampleSlice.arrayBuffer().then(processBuffer).catch(() => {
					this.pinnedHeader = chunkBlob;
				});
			} else if (typeof FileReader !== 'undefined') {
				const reader = new FileReader();
				reader.onload = () => processBuffer(reader.result);
				reader.onerror = () => {
					this.pinnedHeader = chunkBlob;
				};
				reader.readAsArrayBuffer(sampleSlice);
			} else {
				this.pinnedHeader = chunkBlob;
			}
			return;
		}

		const chunkRecord = {
			blob: chunkBlob,
			timestamp: now,
			size: chunkBlob.size
		};

		// 2. If capturing post-incident evidence for an active alert
		if (this.activeIncident) {
			this.activeIncident.postChunks.push(chunkRecord);
			const elapsedPostSec = (now - this.activeIncident.startTime) / 1000;

			console.log(`🎥 [CircularVideoBuffer] Evidence capturing: ${elapsedPostSec.toFixed(1)}s / ${this.activeIncident.targetDuration}s (${this.activeIncident.postChunks.length} post-chunks)`);

			if (elapsedPostSec >= this.activeIncident.targetDuration) {
				this.finalizeEvidenceClip();
			}
		}

		// 3. Add to rolling FIFO circular buffer
		this.rollingChunks.push(chunkRecord);

		// 4. Prune chunks older than maxBufferSeconds (300 seconds)
		const cutoffTime = now - (this.options.maxBufferSeconds * 1000);
		while (this.rollingChunks.length > 0 && this.rollingChunks[0].timestamp < cutoffTime) {
			this.rollingChunks.shift();
		}
	}

	triggerAlert(alertMeta) {
		if (!this.isRecording) return;

		console.log("🚨 [CircularVideoBuffer] Freezing pre-incident window for alert:", alertMeta);

		// If already capturing an incident, extend target duration from the new alert
		if (this.activeIncident) {
			const elapsedSec = (Date.now() - this.activeIncident.startTime) / 1000;
			// Guarantee that post-capture runs for full postAlertSeconds AFTER this new alert
			const newTarget = elapsedSec + this.options.postAlertSeconds;
			if (newTarget > this.activeIncident.targetDuration) {
				this.activeIncident.targetDuration = newTarget;
			}
			console.log(`ℹ️ [CircularVideoBuffer] Incident active! Extended post-capture to ${this.activeIncident.targetDuration.toFixed(1)}s (Alert strike #${alertMeta.strikeNumber || 'N/A'}).`);
			if (!this.activeIncident.additionalAlerts) {
				this.activeIncident.additionalAlerts = [];
			}
			const markerOffset = this.activeIncident.preChunks.reduce((acc, c) => acc + (this.options.timesliceMs / 1000), 0) + elapsedSec;
			this.activeIncident.additionalAlerts.push({
				alertMeta: alertMeta,
				offsetSeconds: markerOffset
			});
			return;
		}

		// Snapshot current rolling buffer as pre-incident evidence
		const incidentStart = Date.now();
		const windowId = (alertMeta && alertMeta.windowId) || `win_${(alertMeta && alertMeta.userTestId) || 'user'}_${incidentStart}`;
		this.activeIncident = {
			windowId: windowId,
			isSplit: false,
			alertMeta: alertMeta,
			additionalAlerts: [],
			preChunks: [...this.rollingChunks],
			postChunks: [],
			startTime: incidentStart,
			targetDuration: this.options.postAlertSeconds
		};
	}

	finalizeEvidenceClip() {
		if (!this.activeIncident) return null;

		const incident = this.activeIncident;
		this.activeIncident = null;

		const allParts = [];
		if (this.pinnedHeader) {
			allParts.push(this.pinnedHeader);
		}
		incident.preChunks.forEach(c => allParts.push(c.blob));
		incident.postChunks.forEach(c => allParts.push(c.blob));

		const evidenceBlob = new Blob(allParts, { type: 'video/webm' });
		const markerOffsetSeconds = incident.preChunks.reduce((acc, c) => acc + (this.options.timesliceMs / 1000), 0);
		const totalDurationSeconds = ((incident.preChunks.length + incident.postChunks.length) * this.options.timesliceMs) / 1000;

		// Build structured alertList containing initial alert and all window extension sub-alerts
		const initialAlertMeta = incident.alertMeta || {};
		const initialAlertDetail = {
			alertType: initialAlertMeta.alertType || 'ALERT',
			alertDescription: initialAlertMeta.description || initialAlertMeta.alertDescription || 'Proctoring alert detected',
			markerOffsetSeconds: markerOffsetSeconds,
			timestamp: initialAlertMeta.timestamp || Date.now(),
			elapsed: initialAlertMeta.elapsed || '00:00',
			testQuestionId: initialAlertMeta.testQuestionId !== undefined ? initialAlertMeta.testQuestionId : null,
			strikeNumber: parseInt(initialAlertMeta.strikeNumber) || 1
		};

		const secondaryAlertDetails = (incident.additionalAlerts || []).map(item => {
			const subMeta = item.alertMeta || {};
			return {
				alertType: subMeta.alertType || 'ALERT',
				alertDescription: subMeta.description || subMeta.alertDescription || 'Proctoring alert detected',
				markerOffsetSeconds: parseFloat(item.offsetSeconds !== undefined ? item.offsetSeconds : (subMeta.markerOffsetSeconds || 0.0)),
				timestamp: subMeta.timestamp || Date.now(),
				elapsed: subMeta.elapsed || '00:00',
				testQuestionId: subMeta.testQuestionId !== undefined ? subMeta.testQuestionId : null,
				strikeNumber: parseInt(subMeta.strikeNumber) || initialAlertDetail.strikeNumber
			};
		});

		const fullAlertList = [initialAlertDetail, ...secondaryAlertDetails];

		const clipIndex = this.evidenceClips.length + 1;
		const clipResult = {
			blob: evidenceBlob,
			sizeBytes: evidenceBlob.size,
			windowId: incident.windowId || `win_${Date.now()}`,
			isSplit: incident.isSplit !== undefined ? !!incident.isSplit : false,
			alertMeta: incident.alertMeta,
			additionalAlerts: incident.additionalAlerts || [],
			alertList: fullAlertList,
			markerOffsetSeconds: markerOffsetSeconds,
			totalDurationSeconds: totalDurationSeconds,
			preChunksCount: incident.preChunks.length,
			postChunksCount: incident.postChunks.length,
			index: clipIndex,
			timestamp: Date.now()
		};

		this.evidenceClips.push(clipResult);
		console.log(`🎬 [CircularVideoBuffer] Evidence Clip #${clipIndex} Assembled! Duration: ${totalDurationSeconds.toFixed(1)}s, Size: ${(evidenceBlob.size / (1024 * 1024)).toFixed(2)} MB, Marker at: ${markerOffsetSeconds.toFixed(1)}s`);

		// Dispatch custom event
		try {
			window.dispatchEvent(new CustomEvent('contestEvidenceReady', { detail: clipResult }));
		} catch (e) {}

		// Invoke callback
		if (typeof this.options.onEvidenceReady === 'function') {
			this.options.onEvidenceReady(clipResult);
		}

		return clipResult;
	}

	flushPendingEvidence() {
		if (this.activeIncident) {
			console.log("⚠️ [CircularVideoBuffer] Flushing active incident prematurely (e.g. test finished)...");
			return this.finalizeEvidenceClip();
		}
		return null;
	}

	isIncidentActive() {
		return !!this.activeIncident;
	}

	captureManualEvidence(alertMeta) {
		if (this.activeIncident) {
			console.log("ℹ️ [CircularVideoBuffer] Active incident already recording, flushing pending evidence...");
			return this.flushPendingEvidence();
		}

		console.log("🎬 [CircularVideoBuffer] Capturing manual evidence snapshot from rolling buffer...");
		const incidentStart = Date.now();
		const windowId = (alertMeta && alertMeta.windowId) || `win_${(alertMeta && alertMeta.userTestId) || 'user'}_${incidentStart}`;
		this.activeIncident = {
			windowId: windowId,
			isSplit: false,
			alertMeta: alertMeta || { alertType: 'MANUAL_SUBMIT', strikeNumber: 0, description: 'Manual video capture' },
			additionalAlerts: [],
			preChunks: [...this.rollingChunks],
			postChunks: [],
			startTime: incidentStart,
			targetDuration: 0
		};
		return this.finalizeEvidenceClip();
	}

	stop() {
		this.flushPendingEvidence();
		if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
			try {
				this.mediaRecorder.stop();
			} catch (e) {}
		}
		this.isRecording = false;
	}

	destroy() {
		this.stop();
		this.pinnedHeader = null;
		this.rollingChunks = [];
		this.activeIncident = null;
		this.evidenceClips = [];
		console.log("🧹 [CircularVideoBuffer] Memory buffer cleared.");
	}

	getEvidenceClips() {
		return this.evidenceClips;
	}
}

export default CircularVideoBuffer;



// WEBPACK FOOTER //
// ./src/util/CircularVideoBuffer.js